import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  advertisingGrowthIntelligenceDev107Api,
  type Dev107Status,
  type SprintDashboard,
  type SprintValidation,
} from '../../api/advertising-growth-intelligence-dev107';

type Tab = 'overview' | 'modules' | 'providers' | 'jobs' | 'gates' | 'status';
type Theme = 'light' | 'dark';

export function AdvertisingGrowthIntelligenceDev107Page() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<SprintDashboard | null>(null);
  const [modules, setModules] = useState<{ key: string; name: string; category: string }[]>([]);
  const [providers, setProviders] = useState<{ key: string; name: string; interface: string }[]>([]);
  const [jobs, setJobs] = useState<{ key: string; name: string; schedule?: string }[]>([]);
  const [gates, setGates] = useState<{ key: string; name: string; blocking: boolean }[]>([]);
  const [status, setStatus] = useState<Dev107Status | null>(null);
  const [validation, setValidation] = useState<SprintValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const card = dark ? 'border-violet-900/40 bg-zinc-900' : 'border-violet-200 bg-white';
  const muted = dark ? 'text-zinc-400' : 'text-slate-500';
  const shell = dark ? 'bg-zinc-950 text-zinc-100' : 'bg-violet-50/20 text-slate-900';
  const accent = dark ? 'text-violet-300' : 'text-violet-700';
  const tabActive = 'bg-violet-600 text-white';

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, mod, prov, jb, gt, st] = await Promise.all([
        advertisingGrowthIntelligenceDev107Api.dashboard(),
        advertisingGrowthIntelligenceDev107Api.intelligenceModules(),
        advertisingGrowthIntelligenceDev107Api.providers(),
        advertisingGrowthIntelligenceDev107Api.backgroundJobs(),
        advertisingGrowthIntelligenceDev107Api.qualityGates(),
        advertisingGrowthIntelligenceDev107Api.status(),
      ]);
      setDashboard(dash); setModules(mod); setProviders(prov); setJobs(jb); setGates(gt); setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load sprint center');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validate = async () => {
    setBusy(true); setActionMsg(null);
    try {
      const result = await advertisingGrowthIntelligenceDev107Api.validate({
        every_campaign_has_health_score: true, recommendations_from_dna: true,
        simulation_operational: true, version_control_functional: true,
        rollback_functional: true, experiments_auto_created: true,
        marketplace_dna_updated: true, attribution_operational: true,
        all_apis_documented: true, tests_pass: true, ci_passes: true,
        performance_budgets_achieved: true,
      });
      setValidation(result);
      setActionMsg(result.valid ? 'Advertising Growth Intelligence passes all quality gates' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Validation failed'); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' }, { id: 'modules', label: 'Intelligence Modules' },
    { id: 'providers', label: 'Providers' }, { id: 'jobs', label: 'Background Jobs' },
    { id: 'gates', label: 'Quality Gates' }, { id: 'status', label: 'Status' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>DEV-000107 · P0 · Profit-Oriented Advertising Growth</p>
          <h1 className="text-2xl font-semibold">Advertising Growth Intelligence Engine</h1>
          <p className={`text-sm ${muted}`}>Observe → Diagnose → Explain → Simulate → Recommend → Approve → Measure → Attribute → Learn</p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/listing-growth-intelligence-dev106" className={`${accent} hover:underline`}>DEV-000106</Link>
            <span className={muted}>→</span>
            <Link to="/advertising/CAMP-SP-001/growth" className={`${accent} hover:underline`}>Live Advertising Workspace</Link>
            <span className={muted}>·</span>
            <Link to="/platform/advertising-growth-engine-prd" className={`${accent} hover:underline`}>PRD-000005</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>{dark ? 'Light' : 'Dark'}</button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-violet-300 bg-violet-50 p-3 text-sm text-violet-700 dark:border-violet-800 dark:bg-violet-950 dark:text-violet-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Not Campaign Management — Profit-Oriented Intelligence</h2>
            <p className={`mt-3 text-sm ${muted}`}>Continuous profit-oriented advertising optimization through measurable intelligence. Every recommendation originates from Marketplace DNA and considers listing quality, inventory, profit, seasonality and competition before recommending any optimization.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Objectives', value: dashboard.objectives },
              { label: 'Intelligence Modules', value: dashboard.intelligence_modules },
              { label: 'Provider Interfaces', value: dashboard.provider_interfaces },
              { label: 'Background Jobs', value: dashboard.background_jobs },
              { label: 'Backend APIs', value: dashboard.backend_apis },
              { label: 'Database Tables', value: dashboard.database_tables },
              { label: 'Quality Gates', value: dashboard.quality_gates },
            ].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
                <p className="mt-1 text-2xl font-semibold">{m.value}</p>
              </div>
            ))}
          </div>
          <button type="button" disabled={busy} onClick={validate} className="rounded-lg bg-violet-600 px-4 py-2 text-sm text-white hover:bg-violet-500 disabled:opacity-50">Validate Quality Gates</button>
          {validation && (
            <div className={`rounded-xl border p-4 ${validation.valid ? 'border-emerald-300' : 'border-violet-300'} ${card}`}>
              <p className="font-semibold">{validation.valid ? 'Engine Valid' : 'Gaps Detected'}</p>
              {validation.gaps && <ul className={`mt-2 list-disc pl-5 text-sm ${muted}`}>{validation.gaps.map((g) => <li key={g}>{g}</li>)}</ul>}
            </div>
          )}
        </div>
      )}

      {tab === 'modules' && (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {modules.map((m) => (
            <div key={m.key} className={`rounded-xl border p-4 ${card}`}>
              <p className="font-semibold">{m.name}</p>
              <p className={`text-xs ${muted}`}>{m.category}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'providers' && (
        <div className="grid gap-2 sm:grid-cols-2">
          {providers.map((p) => (
            <div key={p.key} className={`rounded-xl border p-4 ${card}`}>
              <p className="font-semibold">{p.name}</p>
              <p className={`font-mono text-xs ${accent}`}>{p.interface}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'jobs' && (
        <div className="grid gap-2 sm:grid-cols-2">
          {jobs.map((j) => (
            <div key={j.key} className={`rounded-xl border p-4 ${card}`}>
              <p className="font-semibold">{j.name}</p>
              <p className={`font-mono text-xs ${muted}`}>{j.schedule}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'gates' && (
        <div className="grid gap-2 sm:grid-cols-2">
          {gates.map((g) => (
            <div key={g.key} className={`rounded-xl border p-4 ${card}`}>
              <p className="font-semibold">{g.name}</p>
              {g.blocking && <span className="mt-1 inline-block rounded bg-red-100 px-1.5 py-0.5 text-xs dark:bg-red-900">Blocking</span>}
            </div>
          ))}
        </div>
      )}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-5 ${card}`}>
          <dl className="grid gap-3 sm:grid-cols-2">
            <div><dt className={`text-xs uppercase ${muted}`}>Sprint</dt><dd className="font-semibold">{status.sprint_id}</dd></div>
            <div><dt className={`text-xs uppercase ${muted}`}>DNA Integrated</dt><dd>{status.dna_integrated ? 'Yes' : 'No'}</dd></div>
            <div><dt className={`text-xs uppercase ${muted}`}>Attribution Operational</dt><dd>{status.attribution_operational ? 'Yes' : 'No'}</dd></div>
            <div><dt className={`text-xs uppercase ${muted}`}>Production Ready</dt><dd>{status.production_ready ? 'Yes' : 'No'}</dd></div>
          </dl>
        </div>
      )}
    </div>
  );
}
